import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, Info, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const MODES = [
  { id: "melhorar", label: "Melhorar texto" },
  { id: "completar", label: "Completar" },
  { id: "acolhedor", label: "Tornar mais acolhedor" },
  { id: "estruturar", label: "Estruturar" },
  { id: "simplificar", label: "Simplificar" },
  { id: "personalizar", label: "Personalizar" },
  { id: "criar_atividade", label: "Criar atividade" },
];

// Contextual "Melhorar com IA". onApply(finalText) applies the suggestion to the field.
export default function AIEnhance({ text, patientId, fieldType = "mensagem", onApply, testid = "ai-enhance", label = "Melhorar com IA", onDark = false }) {
  const [open, setOpen] = useState(false);
  const [modes, setModes] = useState(["melhorar"]);
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [used, setUsed] = useState([]);
  const [showCtx, setShowCtx] = useState(false);

  const toggle = (id) =>
    setModes((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const run = async () => {
    if (!text?.trim()) { toast.error("Escreva ou fale algo primeiro."); return; }
    if (modes.length === 0) { toast.error("Escolha ao menos uma ação."); return; }
    setLoading(true);
    setSuggestion("");
    try {
      const { data } = await api.post("/ai/enhance", { text, modes, patient_id: patientId, field_type: fieldType });
      setSuggestion(data.suggestion);
      setUsed(data.context_used || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    onApply?.(suggestion);
    setOpen(false);
    setSuggestion("");
    toast.success("Sugestão aplicada. Revise antes de enviar.");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid={`${testid}-button`}
          className={`rounded-full gap-1.5 ${onDark
            ? "bg-white text-[#0F766E] border-white hover:bg-white/90"
            : "border-[#34D399] text-[#0F766E] hover:bg-[#34D399]/10"}`}>
          <Sparkles size={15} /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-[#1F3B4D]">
            <Sparkles size={18} className="text-[#0F766E]" /> Melhorar com IA
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl bg-[#F8FAFC] border border-[#E0F2FE] p-3 text-sm text-[#1F3B4D]/80 max-h-28 overflow-auto">
            {text || <span className="text-[#94A3B8]">Nenhum texto informado.</span>}
          </div>

          <div className="flex flex-wrap gap-2">
            {MODES.map((m) => (
              <button key={m.id} type="button" data-testid={`${testid}-mode-${m.id}`}
                onClick={() => toggle(m.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  modes.includes(m.id)
                    ? "bg-[#0F766E] text-white border-[#0F766E]"
                    : "bg-white text-[#1F3B4D] border-[#E0F2FE] hover:border-[#34D399]"
                }`}>
                {m.label}
              </button>
            ))}
          </div>

          <Button onClick={run} disabled={loading} data-testid={`${testid}-generate`}
            className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] w-full gap-2">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Gerando…</> : <><Sparkles size={16} /> Gerar sugestão</>}
          </Button>

          {suggestion && (
            <div className="space-y-2 animate-fade-up">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#0F766E]">Sugestão da IA</div>
              <Textarea value={suggestion} onChange={(e) => setSuggestion(e.target.value)}
                data-testid={`${testid}-suggestion`} className="min-h-[160px] rounded-xl border-[#E0F2FE]" />
              {used.length > 0 && (
                <div>
                  <button type="button" onClick={() => setShowCtx(!showCtx)}
                    className="text-xs text-[#60A5FA] hover:underline flex items-center gap-1" data-testid={`${testid}-context-toggle`}>
                    <Info size={13} /> Contexto utilizado pela IA
                  </button>
                  {showCtx && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {used.map((u, i) => <Badge key={i} variant="secondary" className="text-[11px]">{u}</Badge>)}
                    </div>
                  )}
                </div>
              )}
              <p className="text-[11px] text-[#94A3B8]">
                A IA auxilia — você decide. Revise o conteúdo antes de salvar ou enviar.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={apply} disabled={!suggestion} data-testid={`${testid}-apply`}
            className="rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2">
            <Check size={16} /> Usar este texto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
